import { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Container,
  Heading,
  Input,
  Text,
  VStack,
  HStack,
  Alert,
  Spinner,
  Card,
} from '@chakra-ui/react'
import { useVoiceProfile } from '../hooks/useVoiceProfile'

const TWILIO_NUMBER = '(628) 363-1723'

export default function VoiceSettings() {
  const {
    profile,
    isLoading,
    setProfile,
    isSettingProfile,
    deleteProfile,
    isDeletingProfile,
    unlockProfile,
    isUnlocking,
  } = useVoiceProfile()

  const [phoneNumber, setPhoneNumber] = useState('')
  const [pin, setPin] = useState('')
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  // Pre-fill phone number if profile exists
  useEffect(() => {
    if (profile?.phoneNumbers?.[0]) {
      setPhoneNumber(profile.phoneNumbers[0])
    }
  }, [profile])

  const handleSave = async () => {
    setMessage(null)
    try {
      const result = await setProfile({ phoneNumber, pin })
      setMessage({ type: 'success', text: result.message })
      setPin('') // Clear PIN after successful save
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to save settings',
      })
    }
  }

  const handleDelete = async () => {
    setMessage(null)
    try {
      const result = await deleteProfile()
      setMessage({ type: 'success', text: result.message })
      setPhoneNumber('')
      setPin('')
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to remove voice access',
      })
    }
  }

  const handleUnlock = async () => {
    setMessage(null)
    try {
      const result = await unlockProfile()
      setMessage({ type: 'success', text: result.message })
    } catch (err) {
      setMessage({
        type: 'error',
        text: err instanceof Error ? err.message : 'Failed to unlock account',
      })
    }
  }

  if (isLoading) {
    return (
      <Box minH="calc(100vh - 57px)" display="flex" alignItems="center" justifyContent="center">
        <Spinner size="lg" color="blue.500" />
      </Box>
    )
  }

  return (
    <Box minH="calc(100vh - 57px)" bg="gray.50" py={8}>
      <Container maxW="md">
        <VStack gap={6} align="stretch">
          <Box>
            <Heading size="lg" mb={2}>
              Voice Dial-In Settings
            </Heading>
            <Text color="gray.600">
              Call <strong>{TWILIO_NUMBER}</strong> to access your documents by phone.
            </Text>
          </Box>

          {message && (
            <Alert.Root status={message.type === 'success' ? 'success' : 'error'}>
              <Alert.Indicator />
              <Alert.Title>{message.text}</Alert.Title>
            </Alert.Root>
          )}

          <Card.Root>
            <Card.Body>
              <VStack gap={4} align="stretch">
                <Box>
                  <Text fontWeight="medium" mb={1}>
                    Phone Number
                  </Text>
                  <Input
                    placeholder="+1 (555) 123-4567"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                    size="lg"
                  />
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    Enter with country code (e.g., +1 for US)
                  </Text>
                </Box>

                <Box>
                  <Text fontWeight="medium" mb={1}>
                    PIN (4-6 digits)
                  </Text>
                  <Input
                    type="password"
                    placeholder="Enter PIN"
                    value={pin}
                    onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    maxLength={6}
                    size="lg"
                  />
                  <Text fontSize="sm" color="gray.500" mt={1}>
                    You'll enter this PIN when calling in
                  </Text>
                </Box>

                <HStack gap={3} pt={2}>
                  <Button
                    colorScheme="blue"
                    onClick={handleSave}
                    loading={isSettingProfile}
                    disabled={!phoneNumber || !pin || pin.length < 4}
                    flex={1}
                  >
                    Save Settings
                  </Button>
                  {profile?.hasVoiceAccess && (
                    <Button
                      variant="outline"
                      colorScheme="red"
                      onClick={handleDelete}
                      loading={isDeletingProfile}
                    >
                      Disable
                    </Button>
                  )}
                </HStack>
              </VStack>
            </Card.Body>
          </Card.Root>

          {/* Status card */}
          <Card.Root>
            <Card.Body>
              <HStack justify="space-between">
                <Box>
                  <Text fontWeight="medium">Status</Text>
                  {profile?.hasVoiceAccess ? (
                    <Text color="green.600">Voice access enabled</Text>
                  ) : (
                    <Text color="gray.500">Voice access not configured</Text>
                  )}
                </Box>
                {profile?.isLocked && (
                  <Button
                    size="sm"
                    colorScheme="orange"
                    onClick={handleUnlock}
                    loading={isUnlocking}
                  >
                    Unlock Account
                  </Button>
                )}
              </HStack>
              {profile?.hasVoiceAccess && profile?.updatedAt && (
                <Text fontSize="sm" color="gray.500" mt={2}>
                  Last updated: {new Date(profile.updatedAt).toLocaleDateString()}
                </Text>
              )}
            </Card.Body>
          </Card.Root>

          {/* Instructions */}
          <Card.Root bg="blue.50">
            <Card.Body>
              <Text fontWeight="medium" mb={2}>
                How to use voice dial-in:
              </Text>
              <VStack align="start" gap={1}>
                <Text fontSize="sm">1. Call {TWILIO_NUMBER}</Text>
                <Text fontSize="sm">2. Enter your PIN using the keypad</Text>
                <Text fontSize="sm">3. Ask questions about your documents</Text>
                <Text fontSize="sm">4. Say "goodbye" to end the call</Text>
              </VStack>
            </Card.Body>
          </Card.Root>
        </VStack>
      </Container>
    </Box>
  )
}
